import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Edit3, Save, X, Link } from 'lucide-react'
import toast from 'react-hot-toast'
import NoteLinkModal from './NoteLinkModal'

/**
 * TextEditModal Component
 * 
 * Modal component for editing text in flowchart nodes and edges.
 * Similar structure to DrawingModal but designed for text editing.
 */
const TextEditModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialText = '',
  title = 'Edit Text',
  placeholder = 'Enter text...',
  multiline = true,
  maxLength = 500,
  currentNoteId = null // For excluding current note from link selection
}) => {
  const [text, setText] = useState(initialText)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const textareaRef = useRef(null)

  // Update text when initialText changes
  useEffect(() => {
    if (isOpen) {
      setText(initialText)
      // Focus the textarea after a short delay to ensure it's rendered
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus()
          textareaRef.current.select()
        }
      }, 100)
    }
  }, [isOpen, initialText])

  /**
   * Handle text change
   */
  const handleTextChange = (e) => {
    const newText = e.target.value
    if (newText.length <= maxLength) {
      setText(newText)
    }
  }

  /**
   * Handle save
   */
  const handleSave = async () => {
    if (isProcessing) return

    setIsProcessing(true)
    
    try {
      await onSave(text.trim())
      toast.success('Text updated successfully')
    } catch (error) {
      console.error('Failed to save text:', error)
      toast.error('Failed to save text')
    } finally {
      setIsProcessing(false)
    }
  }

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (isProcessing) return
    onClose()
  }

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose()
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSave()
    }
  }

  /**
   * Handle link note selection
   */
  const handleLinkNote = useCallback((noteData) => {
    const linkText = `[${noteData.title}](note:${noteData.noteId})`
    
    // Insert link at cursor position or append to text
    if (textareaRef.current) {
      const textarea = textareaRef.current
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const beforeText = text.substring(0, start)
      const afterText = text.substring(end)
      
      const newText = beforeText + linkText + afterText
      if (newText.length <= maxLength) {
        setText(newText)
        
        // Set cursor position after the inserted link
        setTimeout(() => {
          const newCursorPos = start + linkText.length
          textarea.setSelectionRange(newCursorPos, newCursorPos)
          textarea.focus()
        }, 0)
      } else {
        toast.error(`Text would exceed ${maxLength} character limit`)
      }
    } else {
      // Fallback: append to end
      const newText = text + (text ? '\n' : '') + linkText
      if (newText.length <= maxLength) {
        setText(newText)
      } else {
        toast.error(`Text would exceed ${maxLength} character limit`)
      }
    }
    
    setIsLinkModalOpen(false)
  }, [text, maxLength])

  /**
   * Handle link modal close
   */
  const handleLinkModalClose = useCallback(() => {
    setIsLinkModalOpen(false)
  }, [])

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60] p-2 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-white/40 dark:bg-gray-800/40 backdrop-blur-[40px] rounded-2xl shadow-2xl border border-white/30 dark:border-gray-700/30 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col mx-2 sm:mx-4 animate-in slide-in-from-bottom-4 duration-300">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Edit3 className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
            title="Close"
            disabled={isProcessing}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Text Input Area */}
        <div className="flex-1 p-6 bg-gradient-to-br from-gray-50/40 to-gray-100/40 dark:from-gray-900/40 dark:to-gray-800/40 backdrop-blur-[40px] overflow-hidden">
          <div className="h-full flex flex-col">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Text Content
            </label>
            
            {multiline ? (
              <textarea
                ref={textareaRef}
                value={text}
                onChange={handleTextChange}
                placeholder={placeholder}
                className="flex-1 w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200"
                style={{ minHeight: '200px' }}
                maxLength={maxLength}
              />
            ) : (
              <input
                ref={textareaRef}
                type="text"
                value={text}
                onChange={handleTextChange}
                placeholder={placeholder}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                maxLength={maxLength}
              />
            )}
            
            {/* Character count */}
            <div className="flex justify-between items-center mt-3 text-xs text-gray-500 dark:text-gray-400">
              <span>Use Enter for new lines, Ctrl+Enter to save</span>
              <span className={text.length > maxLength * 0.9 ? 'text-orange-500' : ''}>
                {text.length}/{maxLength}
              </span>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            {/* Link Note Button */}
            <button
              type="button"
              onClick={() => setIsLinkModalOpen(true)}
              className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200 flex items-center space-x-2"
              disabled={isProcessing}
              title="Link to another note"
            >
              <Link className="w-4 h-4" />
              <span>Link Note</span>
            </button>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isProcessing || text.trim() === initialText.trim()}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2"
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Note Link Modal */}
      <NoteLinkModal
        isOpen={isLinkModalOpen}
        onClose={handleLinkModalClose}
        onSelectNote={handleLinkNote}
        currentNoteId={currentNoteId}
      />
    </div>,
    document.body
  )
}

export default TextEditModal
